import React from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  canonicalUrl?: string;
  structuredData?: object;
}

export default function SEOHead({
  title,
  description,
  keywords,
  ogImage = 'https://public.readdy.ai/ai/img_res/8f8b9aaa049b934ed2b245e112eacf67.jpg',
  canonicalUrl,
  structuredData,
}: SEOHeadProps) {
  const fullTitle = `${title} | VyŽehlené`;
  const currentUrl = canonicalUrl || `https://vyzehlene.cz${window.location.pathname}`;

  // Default structured data for LocalBusiness (without opening hours)
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'VyŽehlené',
    description: 'Profesionální služby žehlení v Praze. Vyzvednutí a doručení zdarma. Členství s výhodnými cenami.',
    image: 'https://readdy.ai/api/search-image?query=professional%20ironing%20and%20laundry%20service%20modern%20clean%20background%20with%20perfectly%20pressed%20white%20shirts%20on%20hangers%20bright%20and%20fresh%20atmosphere%20high%20quality%20commercial%20photography%20style&width=1200&height=630&seq=seo-logo-001&orientation=landscape',
    logo: 'https://readdy.ai/api/search-image?query=professional%20ironing%20and%20laundry%20service%20modern%20clean%20background%20with%20perfectly%20pressed%20white%20shirts%20on%20hangers%20bright%20and%20fresh%20atmosphere%20high%20quality%20commercial%20photography%20style&width=1200&height=630&seq=seo-logo-001&orientation=landscape',
    telephone: '+420 777 123 456',
    email: 'info@vyzehlene.cz',
    priceRange: '$$',
    areaServed: {
      '@type': 'City',
      name: 'Praha',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '250',
      bestRating: '5',
      worstRating: '1',
    },
    review: [
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Jana',
        },
        datePublished: '2024-01-15',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        reviewBody: 'Skvělá služba! Prádlo vždy perfektně vyžehlené a doručení přesně na čas. Určitě doporučuji!',
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Petr',
        },
        datePublished: '2024-02-20',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        reviewBody: 'Profesionální přístup, rychlé vyřízení a výborná kvalita. Členství se mi velmi vyplatilo.',
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Marie',
        },
        datePublished: '2024-03-10',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '4',
          bestRating: '5',
        },
        reviewBody: 'Velmi spokojená se službami. Košile vypadají jako nové a ceny jsou férové.',
      },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Služby žehlení',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Žehlení košil',
            description: 'Profesionální žehlení košil s doručením',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Žehlení oblečení',
            description: 'Profesionální žehlení všech typů oblečení',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Vyzvednutí a doručení',
            description: 'Bezplatné vyzvednutí a doručení prádla',
          },
        },
      ],
    },
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={currentUrl} />
      <meta name="last-modified" content={new Date().toISOString()} />

      {/* Geo Tags - Region only, no specific address */}
      <meta name="geo.region" content="CZ-PR" />
      <meta name="geo.placename" content="Praha" />

      {/* Open Graph Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="VyŽehlené" />
      <meta property="og:locale" content="cs_CZ" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </>
  );
}